import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ProductInputQuantityConfigService } from '../../services/product-input-quantity-config/product-input-quantity-config.service';
import { AuthService } from '../../services/auth/auth.service';
import { Product } from '../../models';
import { CustomerProductPrice, Price } from '../../models/price';
import { Availability, SimplifiedAvailability } from '../../models/availability';
import { first } from 'rxjs';

export const generateId = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

@Component({
  selector: 'lib-product-quantity-input',
  templateUrl: './product-quantity-input.component.html',
  styleUrl: './product-quantity-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductQuantityInputComponent),
      multi: true
    }
  ],
  standalone: false
})
export class ProductQuantityInputComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input()
  public productId: string;

  @Input()
  public product: Product;

  @Input()
  public price: CustomerProductPrice;

  @Input()
  public availability: Availability | SimplifiedAvailability;

  isAnonymous: boolean;
  isUser: boolean;
  isSales: boolean;
  isAdmin: boolean;
  isActuator: boolean;
  roles: string[];

  showDecimals: number;
  allowManualQtyInputOutsideStepSizes: boolean;
  allowPurchaseOverAvailability: boolean;
  maxValue: number;
  minValue: number;
  stepAmount: number;

  @Input()
  public renderStatic = false;

  @Input()
  public required = false;

  @Input()
  public disabled = false;

  @Input()
  public extraClass: string = '';

  @Input()
  public inputId: string = generateId();

  @Input()
  public selectOnFocus = false;

  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
  protected _value: number;

  public translateTag: string;

  public onChange: (val: number) => void = _ => {
    console.log(_);
  };
  // public onTouch: () => void = () => { };
  public onTouched: () => void = () => {};

  get value() {
    // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
    return this._value;
  }

  set value(val: number) {
    if (!this.allowManualQtyInputOutsideStepSizes) {
      val = this.snapToStep(val, this.stepAmount);
    }
    this._value = val;
    this.onChange(val);
  }

  writeValue(obj: number): void {
    if (obj !== undefined && obj !== null) {
      const coercedValue = Number(obj.toFixed(this.showDecimals ?? 0));
      this.value = coercedValue;
    }
  }

  registerOnChange(fn: (val: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  @ViewChild('quantityInput') quantityInputRef: ElementRef<HTMLInputElement>;

  focusInput(): void {
    const input = this.quantityInputRef?.nativeElement;
    input?.focus();

    if (this.selectOnFocus) {
      setTimeout(() => input?.select(), 0);
    }
  }

  onInputFocus(event: FocusEvent): void {
    if (!this.selectOnFocus) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    setTimeout(() => input?.select(), 0);
  }

  @Output() keyTyped = new EventEmitter<KeyboardEvent>();

  constructor(
    protected productInputQuantityConfigService: ProductInputQuantityConfigService,
    protected authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isAnonymous = roles.indexOf('ANONYMOUS') > -1;
        this.isUser = roles.indexOf('USER') > -1 && roles.indexOf('SALES') === -1 && roles.indexOf('ADMIN') === -1;
        this.isSales = roles.indexOf('SALES') > -1;
        this.isAdmin = roles.indexOf('ADMIN') > -1;
        this.isActuator = roles.indexOf('ACTUATOR') > -1;
        this.getProductQuantityInputData();
        if (!this.value) {
          this.value = this.productInputQuantityConfigService.getRecomendedInitOrderQuantity(
            this.productId,
            this.product,
            this.price
          );
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getProductQuantityInputData();
    if (changes.productId) {
      if (!this.value) {
        this.value = this.productInputQuantityConfigService.getRecomendedInitOrderQuantity(
          this.productId,
          this.product,
          this.price
        );
      }
    }
  }

  getProductQuantityInputData(): void {
    this.allowManualQtyInputOutsideStepSizes =
      this.productInputQuantityConfigService.getAllowManualQtyInputOutsideStepSizes(
        this.productId,
        this.product,
        this.price,
        this.isAnonymous,
        this.isUser,
        this.isSales,
        this.isAdmin,
        this.isActuator
      );
    this.minValue = this.productInputQuantityConfigService.getMinimumOrderQuantity(
      this.productId,
      this.product,
      this.price
    );
    this.stepAmount = this.productInputQuantityConfigService.getStepAmount(
      this.productId,
      this.product,
      this.price,
      this.roles
    );
    this.showDecimals = this.productInputQuantityConfigService.showDecimals(this.productId, this.product);
    this.allowPurchaseOverAvailability = this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(
      this.value,
      this.availability,
      this.product,
      this.roles
    );
    this.maxValue = this.productInputQuantityConfigService.getMaxOrderQuantity(this.productId, this.product);
  }

  snapToStep(value: number, step: number): number {
    if (!step || step === 0) {
      return value;
    }

    const min = this.minValue || 0;

    // If value is at or below minimum, return minimum
    if (value <= min) {
      return parseFloat(min.toFixed(this.showDecimals ?? 0));
    }

    // Snap to multiples of step: 0, step, 2*step, 3*step...
    // This gives sequences like: min=1, step=36 → 1, 36, 72, 108...
    const stepsFromZero = Math.round(value / step);
    const snapped = stepsFromZero * step;

    // Ensure we don't go below minimum
    return parseFloat(Math.max(snapped, min).toFixed(this.showDecimals ?? 0));
  }

  getDecimalPlaces(num: number): number {
    const match = num.toString().match(/(?:\.(\d+))?$/);
    return match && match[1] ? match[1].length : 0;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.incrementValue();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.decrementValue();
    }
    this.keyTyped.emit(event);
  }

  incrementValue(): void {
    const currentValue = this.value || 0;
    const step = this.stepAmount || 1;

    // If no current value, set to recommended initial quantity
    if (!currentValue) {
      const toAdd = Math.max(this.minValue || 0, step, currentValue);
      if (
        !this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(
          toAdd,
          this.availability,
          this.product,
          this.roles
        )
      ) {
        if (this.productInputQuantityConfigService.purchaseOverAvailabilitySetMaxQty) {
          const maxValue = this.productInputQuantityConfigService.getMaximumOrderQuantityFromAvailability(
            this.value,
            toAdd,
            this.availability,
            this.product,
            this.roles
          );
          if (maxValue !== this.value) {
            this.value = maxValue;
          }
        }
        return;
      }
      this.value = toAdd;
      return;
    }

    // Check if we need to snap to step first
    const differenceToStep = step - Number(Number(currentValue % step).toFixed(this.showDecimals ?? 0));
    if (differenceToStep && differenceToStep !== step) {
      const snapValue = currentValue + differenceToStep;
      if (
        !this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(
          snapValue,
          this.availability,
          this.product,
          this.roles
        )
      ) {
        if (this.productInputQuantityConfigService.purchaseOverAvailabilitySetMaxQty) {
          const maxValue = this.productInputQuantityConfigService.getMaximumOrderQuantityFromAvailability(
            this.value,
            snapValue,
            this.availability,
            this.product,
            this.roles
          );
          if (maxValue !== this.value) {
            this.value = maxValue;
          }
        }
        return;
      }
      this.value = this.snapToStep(snapValue, step);
      return;
    }

    // Normal increment
    const newValue = currentValue + step;

    // Check availability
    if (
      !this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(
        newValue,
        this.availability,
        this.product,
        this.roles
      )
    ) {
      if (this.productInputQuantityConfigService.purchaseOverAvailabilitySetMaxQty) {
        const maxValue = this.productInputQuantityConfigService.getMaximumOrderQuantityFromAvailability(
          this.value,
          newValue,
          this.availability,
          this.product,
          this.roles
        );
        if (maxValue !== this.value) {
          this.value = maxValue;
        }
      }
      return;
    }

    // Check max order quantity
    if (this.maxValue !== null && this.maxValue !== undefined && !isNaN(this.maxValue) && newValue > this.maxValue) {
      return;
    }

    this.value = this.snapToStep(newValue, step);
  }

  decrementValue(): void {
    const currentValue = this.value || 0;
    const step = this.stepAmount || 1;
    const min = this.minValue || 0;

    // Check if decrementing by step would still be >= minimum
    if (currentValue - step >= min) {
      // Check if we need to snap to step first
      const differenceToStep = step - Number(Number(currentValue % step).toFixed(this.showDecimals ?? 0));
      if (differenceToStep && differenceToStep !== step) {
        const quantityToSubtract = step - differenceToStep;
        const newValue = currentValue - quantityToSubtract;
        this.value = this.snapToStep(newValue, step);
        return;
      }

      // Normal decrement
      const newValue = currentValue - step;
      this.value = this.snapToStep(newValue, step);
    } else {
      // Decrementing would go below minimum - handle based on step alignment
      const differenceToStep = step - Number(Number(currentValue % step).toFixed(this.showDecimals ?? 0));
      if (differenceToStep && differenceToStep !== step) {
        const quantityToSubtract = step - differenceToStep;
        const newValue = currentValue - quantityToSubtract;
        if (newValue >= min) {
          this.value = this.snapToStep(newValue, step);
          return;
        }
      }

      // Set to minimum or 0
      this.value = min;
    }
  }
}
